import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import type { AppFolder, AssetFolder } from '../../domain';
import { useFolders } from '../../queries';
import { ApiResponseError } from '../../api/envelope';
import { allAppAssetsFolder, filterAppFolders, humanizeAppType } from './assetUtils';

interface Props {
  /** Composite "{appAgentType}/{sessionId}" of the selected folder, or null. */
  selectedId: string | null;
  onSelect: (folder: AssetFolder, app: AppFolder) => void;
}

/**
 * Left-rail asset folder navigator. Renders the CP3 hierarchy as
 * app -> session two-level tree. Apps expand/collapse; sessions
 * inside are click-to-select. Search filters by app name OR session
 * name OR session id.
 */
export const FoldersPanel: React.FC<Props> = ({ selectedId, onSelect }) => {
  const { data, isLoading, error } = useFolders();
  const [query, setQuery] = useState('');
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());

  const filteredApps = useMemo(() => {
    if (!data?.apps) return [];
    return filterAppFolders(data.apps, query);
  }, [data?.apps, query]);

  // When the user searches, auto-expand every matching app so the matched
  // sessions are visible without an extra click.
  React.useEffect(() => {
    if (!query.trim()) return;
    setExpandedApps(new Set(filteredApps.map(a => a.appAgentType)));
  }, [query, filteredApps]);

  // Expand the app that holds the selection when it is set from outside (after
  // an import, the page selects that app's "All assets" folder).
  React.useEffect(() => {
    const app = selectedId?.split('/')[0];
    if (!app) return;
    setExpandedApps(prev => (prev.has(app) ? prev : new Set(prev).add(app)));
  }, [selectedId]);

  const toggleApp = (appAgentType: string) => {
    setExpandedApps(prev => {
      const next = new Set(prev);
      if (next.has(appAgentType)) next.delete(appAgentType);
      else next.add(appAgentType);
      return next;
    });
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
      aria-label="Asset folders"
    >
      <Box sx={{ p: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search folders"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQuery('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={20} />
          </Stack>
        )}

        {error && (
          <Box sx={{ px: 1.5 }}>
            <Alert severity="error" sx={{ fontSize: 12 }}>
              {error instanceof ApiResponseError
                ? `${error.code}: ${error.message}`
                : error instanceof Error
                  ? error.message
                  : 'Failed to load folders.'}
            </Alert>
          </Box>
        )}

        {data && filteredApps.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 1.5, display: 'block' }}
          >
            {query.trim() ? 'No folders match the search.' : 'No assets yet.'}
          </Typography>
        )}

        {filteredApps.map(app => {
          const expanded = expandedApps.has(app.appAgentType);
          // "All assets" first: the only place session-less assets (imports) show.
          const folders = [allAppAssetsFolder(app.appAgentType, app.assetCount), ...app.folders];
          return (
            <Box key={app.appAgentType}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={() => toggleApp(app.appAgentType)}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {expanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
                <FolderOutlinedIcon fontSize="small" sx={{ opacity: 0.8 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {humanizeAppType(app.appAgentType)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {app.assetCount}
                </Typography>
              </Stack>

              {expanded &&
                folders.map(folder => {
                  const isSelected = folder.id === selectedId;
                  return (
                    <Stack
                      key={folder.id}
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      onClick={() => onSelect(folder, app)}
                      sx={{
                        pl: 5,
                        pr: 1.5,
                        py: 0.5,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        borderLeft: theme =>
                          isSelected
                            ? `2px solid ${theme.palette.primary.main}`
                            : '2px solid transparent',
                        '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                      }}
                    >
                      {isSelected ? (
                        <FolderOpenIcon fontSize="inherit" sx={{ opacity: 0.8 }} />
                      ) : (
                        <ImageOutlinedIcon fontSize="inherit" sx={{ opacity: 0.6 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {folder.sessionName ?? folder.sessionId}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {folder.assetCount}
                      </Typography>
                    </Stack>
                  );
                })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
